/**
 * "केवल बूथ न० डालने से बूथ का नाम आ जायेगा": type 45 / 45A and the booth's name, panchayat and
 * village come straight from the local database, with or without network.
 */
import { useMemo, useState } from "react";
import { FIELD_SECTIONS } from "@npac/rules";
import { filterBooths, findBoothByCode } from "@core/boothCode";
import { locationView, sectionView } from "@core/status";
import type { Booth, OutboxItem } from "@core/types";
import { formatTime } from "../i18n";
import { useApp, useQuery, useSession } from "../ui/context";
import { href, navigate } from "../ui/router";
import { Banner, Button, StatusDot, Toggle } from "../ui/components";
import { MyPoints } from "./MyPoints";

export function BoothsScreen() {
  const { app, t, lang } = useApp();
  const { user, assemblyId } = useSession();
  const [query, setQuery] = useState("");
  const [onlyMine, setOnlyMine] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const data = useQuery(async () => {
    if (!assemblyId) return null;
    const [booths, outbox, fetchedAt] = await Promise.all([
      app.core.store.listBooths(assemblyId),
      app.core.store.outboxForAssembly(assemblyId),
      app.core.store.boothsFetchedAt(assemblyId),
    ]);
    return { booths, outbox, fetchedAt };
  }, [assemblyId]);

  const booths = data?.booths ?? [];
  const hasAssigned = user?.role === "surveyor" && user.allowedBooths != null;
  const exact = useMemo(() => (query.trim() ? findBoothByCode(booths, query) : null), [booths, query]);
  const list = useMemo(() => {
    let l = filterBooths(booths, query);
    if (hasAssigned && onlyMine) l = l.filter((b) => app.canEdit(b));
    return l;
  }, [booths, query, hasAssigned, onlyMine, app]);
  const byBooth = useMemo(() => {
    const m = new Map<string, OutboxItem[]>();
    for (const o of data?.outbox ?? []) m.set(o.boothCode, [...(m.get(o.boothCode) ?? []), o]);
    return m;
  }, [data?.outbox]);

  const refresh = async () => {
    setRefreshing(true);
    setMsg(null);
    const r = await app.core.refreshBooths();
    setRefreshing(false);
    if (r.ok) setMsg({ kind: "ok", text: t.boothsDownloaded(r.count) });
    else if (r.reason !== "unauthenticated") setMsg({ kind: "error", text: t.downloadFailed });
  };

  const open = (b: Booth) => {
    void app.rememberBooth(b.code);
    navigate({ name: "booth", code: b.code });
  };

  return (
    <div>
      <MyPoints />
      <label className="label" htmlFor="booth-code">
        {t.boothNo}
      </label>
      <input
        id="booth-code"
        className="input code-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="45 / 45A"
        aria-describedby="booth-code-hint"
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="go"
        onKeyDown={(e) => e.key === "Enter" && exact && open(exact)}
        data-testid="booth-code"
      />
      <div id="booth-code-hint" className="hint">
        {t.boothNoHint}
      </div>
      {query.trim() ? (
        exact ? (
          <div className={`match${app.canEdit(exact) ? "" : " match-view"}`} data-testid="booth-match">
            <div style={{ fontWeight: 800 }}>
              {t.boothNo} {exact.code}
            </div>
            <div className="match-name">{exact.name}</div>
            <div>
              {t.panchayat}: {exact.panchayat || t.emptyValue} • {t.village}: {exact.village || t.emptyValue}
            </div>
            {!app.canEdit(exact) ? (
              <div className="view-only" data-testid="not-assigned">
                ⚠ {t.notAssigned}
              </div>
            ) : null}
            <div style={{ marginTop: 10 }}>
              <Button onClick={() => open(exact)} testId="booth-open">
                {t.open} →
              </Button>
            </div>
          </div>
        ) : list.length === 0 ? (
          <p className="error" role="alert">
            {t.boothNotFound}
          </p>
        ) : null
      ) : null}

      <div style={{ height: 16 }} />
      {msg ? <Banner kind={msg.kind}>{msg.text}</Banner> : null}
      {data && booths.length === 0 && !refreshing ? <Banner kind="warn">{t.noBoothsCached}</Banner> : null}

      <div className="row" style={{ alignItems: "center" }}>
        <div>
          <b>{t.boothCount(list.length)}</b>
          {data?.fetchedAt ? <div className="muted">{t.updatedAt(formatTime(data.fetchedAt, lang))}</div> : null}
        </div>
        <Button variant="secondary" onClick={refresh} busy={refreshing}>
          ⟳ {t.refresh}
        </Button>
      </div>
      {hasAssigned ? <Toggle label={t.myBooths} checked={onlyMine} onChange={setOnlyMine} /> : null}

      <ul className="blist" data-testid="booth-list">
        {list.map((b) => {
          const ob = byBooth.get(b.code) ?? [];
          const loc = locationView(b, ob);
          const editable = app.canEdit(b);
          return (
            <li key={b.code}>
              <a href={href({ name: "booth", code: b.code })} onClick={() => void app.rememberBooth(b.code)} style={editable ? undefined : { opacity: 0.75 }}>
                <span className="bcode">{b.code}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="bname">{b.name}</span>
                  <span className="bmeta" style={{ display: "block" }}>
                    {[b.panchayat, b.village].filter(Boolean).join(" • ") || t.emptyValue}
                  </span>
                  <span className="dots">
                    {FIELD_SECTIONS.map((s) => (
                      <StatusDot key={s} status={sectionView(b, s, ob).status} t={t} />
                    ))}
                    <span aria-label={loc}>{loc === "shared" ? "📍" : loc === "waiting" ? "⏳📍" : loc === "failed" ? "⚠📍" : ""}</span>
                    {!editable ? <span className="view-only">{t.viewOnly}</span> : null}
                  </span>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
